import {Box, Grid, Heading, Stack, ThemeProvider} from '@primer/react-brand'

import {
  ContentfulCards,
  ContentfulCtaBanner,
  ContentfulFaqGroup,
  ContentfulHero,
  ContentfulPillars,
  ContentfulRiver,
  ContentfulRiverBreakout,
  ContentfulSectionIntro,
  ContentfulTestimonials,
} from '../../components/contentful'
import {isRiverBreakout, type F2Page} from '../../lib/types/contentful'

export type F2TemplateProps = {
  page: F2Page
}

export function F2Template({page}: F2TemplateProps) {
  const {template} = page.fields

  return (
    <ThemeProvider
      colorMode={page.fields.settings?.fields.colorMode ?? 'light'}
      style={{backgroundColor: 'var(--brand-color-canvas-default)'}}
    >
      <Grid>
        <Grid.Column>
          <ContentfulHero data-hpc component={template.fields.hero} />
        </Grid.Column>

        {(template.fields.sectionIntro || template.fields.pillars) && (
          <Grid.Column>
            <Grid className="F2Template__IntroduceSection">
              {template.fields.sectionIntro && (
                <Grid.Column
                  span={{medium: template.fields.sectionIntro.fields.align === 'center' ? 8 : 9}}
                  start={{medium: template.fields.sectionIntro.fields.align === 'center' ? 3 : 1}}
                >
                  <ContentfulSectionIntro component={template.fields.sectionIntro} fullWidth />
                </Grid.Column>
              )}

              {template.fields.pillars && template.fields.pillars.length > 0 && (
                <Grid.Column>
                  <ContentfulPillars pillars={template.fields.pillars} />
                </Grid.Column>
              )}
            </Grid>
          </Grid.Column>
        )}

        {template.fields.rivers.length > 0 && (
          <Grid.Column>
            <Box paddingBlockStart={48} paddingBlockEnd={80}>
              {template.fields.rivers.map(river =>
                isRiverBreakout(river) ? (
                  <ContentfulRiverBreakout component={river} key={river.sys.id} />
                ) : (
                  <ContentfulRiver component={river} key={river.sys.id} />
                ),
              )}
            </Box>
          </Grid.Column>
        )}
      </Grid>

      <Box backgroundColor="subtle" paddingBlockStart={128} paddingBlockEnd={128}>
        <Grid className="F2Template__TrailingSection">
          {template.fields.testimonials && template.fields.testimonials.length > 0 && (
            <Grid.Column>
              <ContentfulTestimonials testimonials={template.fields.testimonials} />
            </Grid.Column>
          )}

          <Grid.Column>
            <ContentfulCtaBanner component={template.fields.ctaBanner} />
          </Grid.Column>

          {template.fields.cardsHeading && template.fields.cards && (
            <Grid.Column>
              <Stack direction="vertical" padding="none" gap={64} alignItems="center">
                <Heading as="h3" size="3">
                  {template.fields.cardsHeading}
                </Heading>

                <ContentfulCards cards={template.fields.cards} />
              </Stack>
            </Grid.Column>
          )}

          {template.fields.faq && (
            <Grid.Column>
              <ContentfulFaqGroup component={template.fields.faq} />
            </Grid.Column>
          )}
        </Grid>
      </Box>
    </ThemeProvider>
  )
}

try{ F2Template.displayName ||= 'F2Template' } catch {}